import React, { FC, ReactElement, ReactNode, Suspense } from 'react';
import { useSuspenseQuery } from '@apollo/client';
import { useTranslations } from '../../../../hooks/useTranslations';
import Spinner from '../../../ui/Spinner/Spinner';
import {
  HugeHeader,
  SemibigHeader,
  MediumHeader,
} from '../../../ui/Header/Header';
import {
  MomentCalendarTranslationKeysInput,
  QueryOrderTrackingArgs,
  QueryFormatEddArgs,
  FormatBlockEddQuery,
} from '../../../../../../__generated__/loma';
import useOrderTracking from '../../../../hooks/useOrderTracking';
import { useTheme } from '../../../../hooks/useTheme';
import EddDashWithTheme from './BlockEddDash';
import getFormattedEdd from '../../../../../../graphql/getFormattedEdd';
import { useLogEdd } from './hooks/useLogEdd';
import styles from './BlockEdd.module.css';
import { usePageContext } from '../../../../hooks/PageContext/usePageContext';

export interface BlockEddProps
  extends Pick<QueryOrderTrackingArgs, 'orderNumbers'> {
  featureName?: string;
  internationalDateFormatting?: boolean | null;
}

enum DateTokenType {
  ARIA_LABEL = 'ARIA_LABEL',
  DAY_OF_MONTH = 'DAY_OF_MONTH',
  DAY_OF_WEEK = 'DAY_OF_WEEK',
  MONTH = 'MONTH',
}

// Typescript can only narrow based on literals, meaning we can't determine that
// an arbitrary variable represents a key of an object. In other words, this won't narrow:
// if (token in DateTokenType). But this will: if ("DAY_OF_MONTH" in DateTokenType).
// This utility function asserts that the value of an arbitrary variable is
// in fact a key of an object T if `key in obj` is true
function isIn<T extends Record<PropertyKey, unknown>>(
  key: PropertyKey,
  obj: T,
): key is keyof T {
  return key in obj;
}

const DEFAULT_EDD_ORDER = `${DateTokenType.DAY_OF_WEEK},${DateTokenType.MONTH},${DateTokenType.DAY_OF_MONTH}`;

const eddFormatTranslationKeysStandard: MomentCalendarTranslationKeysInput[] = [
  {
    sameDay: 'blockEddTopSameDayDateFormat',
    lastDay: 'blockEddTopLastDayDateFormat',
    nextDay: 'blockEddTopNextDayDateFormat',
    lastWeek: 'blockEddTopLastWeekDateFormat',
    nextWeek: 'blockEddTopNextWeekDateFormat',
    sameElse: 'blockEddTopSameElseDateFormat',
  },
  {
    sameDay: 'blockEddMiddleSameDayDateFormat',
    lastDay: 'blockEddMiddleLastDayDateFormat',
    nextDay: 'blockEddMiddleNextDayDateFormat',
    lastWeek: 'blockEddMiddleLastWeekDateFormat',
    nextWeek: 'blockEddMiddleNextWeekDateFormat',
    sameElse: 'blockEddMiddleSameElseDateFormat',
  },
  {
    sameDay: 'blockEddBottomSameDayDateFormat',
    lastDay: 'blockEddBottomLastDayDateFormat',
    nextDay: 'blockEddBottomNextDayDateFormat',
    lastWeek: 'blockEddBottomLastWeekDateFormat',
    nextWeek: 'blockEddBottomNextWeekDateFormat',
    sameElse: 'blockEddBottomSameElseDateFormat',
  },
];

const eddFormatTranslationKeysLocaleAware: MomentCalendarTranslationKeysInput[] =
  [
    {
      sameDay: 'blockEddDayOfWeekSameDayDateFormat',
      lastDay: 'blockEddDayOfWeekLastDayDateFormat',
      nextDay: 'blockEddDayOfWeekNextDayDateFormat',
      lastWeek: 'blockEddDayOfWeekLastWeekDateFormat',
      nextWeek: 'blockEddDayOfWeekNextWeekDateFormat',
      sameElse: 'blockEddDayOfWeekSameElseDateFormat',
    },
    {
      sameDay: 'blockEddMonthSameDayDateFormat',
      lastDay: 'blockEddMonthLastDayDateFormat',
      nextDay: 'blockEddMonthNextDayDateFormat',
      lastWeek: 'blockEddMonthLastWeekDateFormat',
      nextWeek: 'blockEddMonthNextWeekDateFormat',
      sameElse: 'blockEddMonthSameElseDateFormat',
    },
    {
      sameDay: 'blockEddDayOfMonthSameDayDateFormat',
      lastDay: 'blockEddDayOfMonthLastDayDateFormat',
      nextDay: 'blockEddDayOfMonthNextDayDateFormat',
      lastWeek: 'blockEddDayOfMonthLastWeekDateFormat',
      nextWeek: 'blockEddDayOfMonthNextWeekDateFormat',
      sameElse: 'blockEddDayOfMonthSameElseDateFormat',
    },
  ];

const BlockEdd: FC<BlockEddProps> = ({
  featureName,
  internationalDateFormatting,
}): ReactElement | null => {
  const { spinnerPrimaryNone } = useTheme();

  const { state } = usePageContext();
  const { localeLanguage, localeCountry, retailerMoniker, versionName } = state;

  const { blockEddDateTokenOrder: blockEddDateTokenOrderRaw } =
    useTranslations();

  const { getCurrentShipment } = useOrderTracking();
  const currentShipment = getCurrentShipment();

  const { displayDate, displayDateRange, scheduling } = currentShipment || {};
  const schedulable = scheduling?.isSchedulable && !scheduling.isScheduled;

  const eddFormatTranslationKeys: QueryFormatEddArgs['eddCombos'][0]['eddFormatTranslationKeys'] =
    [
      {
        sameDay: 'blockEddAriaLabelSameDayDateFormat',
        lastDay: 'blockEddAriaLabelLastDayDateFormat',
        nextDay: 'blockEddAriaLabelNextDayDateFormat',
        lastWeek: 'blockEddAriaLabelLastWeekDateFormat',
        nextWeek: 'blockEddAriaLabelNextWeekDateFormat',
        sameElse: 'blockEddAriaLabelSameElseDateFormat',
      },
      {
        sameDay: 'blockEddDayOfWeekShortSameDayDateFormat',
        lastDay: 'blockEddDayOfWeekShortLastDayDateFormat',
        nextDay: 'blockEddDayOfWeekShortNextDayDateFormat',
        lastWeek: 'blockEddDayOfWeekShortLastWeekDateFormat',
        nextWeek: 'blockEddDayOfWeekShortNextWeekDateFormat',
        sameElse: 'blockEddDayOfWeekShortSameElseDateFormat',
      },
      ...(internationalDateFormatting
        ? eddFormatTranslationKeysLocaleAware
        : eddFormatTranslationKeysStandard),
    ];

  const eddCombos: QueryFormatEddArgs['eddCombos'] = [];
  const eddBegin = displayDateRange?.begin || displayDate;

  useLogEdd(currentShipment, featureName);

  if (eddBegin) {
    eddCombos.push({
      displayDate: eddBegin,
      eddFormatTranslationKeys,
      dateOnly: false,
    });
  }
  if (displayDateRange?.end) {
    eddCombos.push({
      displayDate: displayDateRange.end,
      eddFormatTranslationKeys,
      dateOnly: false,
    });
  }

  const { data } = useSuspenseQuery<FormatBlockEddQuery, QueryFormatEddArgs>(
    getFormattedEdd,
    {
      variables: {
        retailerMoniker,
        versionName,
        localeLanguage,
        localeCountry,
        eddCombos,
      },
      skip: !currentShipment,
    },
  );

  const { formatEdd } = data ?? {};

  if (!currentShipment || schedulable) return null;

  const [eddBeginFormatted, eddEndFormatted] = formatEdd?.formattedEdds || [];

  let dateValuesMappings: (Record<DateTokenType, string> | null)[] | null =
    null;
  if (eddEndFormatted) {
    // Use the short "day of week" translation for date range
    const [beginAriaLabel, beginDayOfWeekShort, , beginMonth, beginDayOfMonth] =
      eddBeginFormatted;
    const [endAriaLabel, endDayOfWeekShort, , endMonth, endDayOfMonth] =
      eddEndFormatted;

    const begin =
      beginAriaLabel && beginDayOfWeekShort && beginMonth && beginDayOfMonth
        ? {
            ARIA_LABEL: beginAriaLabel,
            DAY_OF_MONTH: beginDayOfMonth,
            DAY_OF_WEEK: beginDayOfWeekShort,
            MONTH: beginMonth,
          }
        : null;
    const end =
      endAriaLabel && endDayOfWeekShort && endMonth && endDayOfMonth
        ? {
            ARIA_LABEL: endAriaLabel,
            DAY_OF_MONTH: endDayOfMonth,
            DAY_OF_WEEK: endDayOfWeekShort,
            MONTH: endMonth,
          }
        : null;

    dateValuesMappings = [begin, end];
  } else {
    // Use the long "day of week" translation
    const [ariaLabel, , dayOfWeek, month, dayOfMonth] = eddBeginFormatted;
    const begin =
      ariaLabel && dayOfWeek && month && dayOfMonth
        ? {
            ARIA_LABEL: ariaLabel,
            DAY_OF_MONTH: dayOfMonth,
            DAY_OF_WEEK: dayOfWeek,
            MONTH: month,
          }
        : null;

    dateValuesMappings = [begin, null];
  }

  if (!dateValuesMappings[0]) return null;

  const blockEddDateTokenOrder =
    internationalDateFormatting && typeof blockEddDateTokenOrderRaw === 'string'
      ? blockEddDateTokenOrderRaw || DEFAULT_EDD_ORDER
      : DEFAULT_EDD_ORDER;
  const blockEddDateTokenOrderArray = blockEddDateTokenOrder.split(',');

  const renderEdd: (
    dateValuesMapping: Record<DateTokenType, string>,
  ) => ReactNode = (dateValuesMapping) => {
    if (!dateValuesMapping) return null;

    return blockEddDateTokenOrder.split(',').map((token) => {
      let value = null;
      if (isIn<Record<DateTokenType, string>>(token, dateValuesMapping)) {
        value = dateValuesMapping[token];
      }
      if (!value) return null;

      switch (token) {
        case DateTokenType.DAY_OF_MONTH:
          return (
            value && (
              <HugeHeader
                as="p"
                containerCustomStyle={{ marginTop: '0.5rem' }}
                key={token}
              >
                {value}
              </HugeHeader>
            )
          );
        case DateTokenType.DAY_OF_WEEK:
          return (
            value && (
              <SemibigHeader as="p" key={token}>
                {value}
              </SemibigHeader>
            )
          );
        case DateTokenType.MONTH:
          return (
            value && (
              <MediumHeader
                as="p"
                containerCustomStyle={{ textTransform: 'uppercase' }}
                key={token}
              >
                {value}
              </MediumHeader>
            )
          );
        default:
          return null;
      }
    });
  };

  const [begin, end] = dateValuesMappings;

  // To make sure the hyphen is always in line with the day of month value, the
  // hyphen is given the same margin and lineHeight as the day of month.
  // Then we flex it at the start, center, or end depending on where DAY_OF_MONTH
  // is in the blockEddDateTokenOrderArray
  const containerFlexAlignItems = ['flex-start', 'center', 'flex-end'][
    blockEddDateTokenOrderArray.indexOf(DateTokenType.DAY_OF_MONTH)
  ];

  const loading = (
    <div>
      <Spinner s={spinnerPrimaryNone} />
    </div>
  );

  return (
    <Suspense fallback={loading}>
      <div
        data-test="eddDisplayContainer"
        className={styles.container}
        style={{
          alignItems: containerFlexAlignItems,
        }}
      >
        {begin && (
          <div aria-label={begin.ARIA_LABEL}>
            <span aria-hidden="true">{renderEdd(begin)}</span>
          </div>
        )}
        {end && (
          <>
            <EddDashWithTheme />
            <div aria-label={end.ARIA_LABEL}>
              <span aria-hidden="true">{renderEdd(end)}</span>
            </div>
          </>
        )}
      </div>
    </Suspense>
  );
};

export default BlockEdd;
